import React, { useState, useEffect, useCallback } from 'react';
import { PayPalScriptProvider, PayPalButtons } from '@paypal/react-paypal-js';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';
import { toast } from 'react-toastify';

const Credits = () => {
  const { user, updateCredits, unlockSenderId, needsCredits, setNeedsCredits } = useAuth();
  const [creditPacks, setCreditPacks] = useState([]);
  const [selectedPack, setSelectedPack] = useState(null);
  const [includeUnlock, setIncludeUnlock] = useState(false);
  const [paypalClientId, setPaypalClientId] = useState('');
  const [loading, setLoading] = useState(true);
  const [transactions, setTransactions] = useState([]);
  const [unlockStatusLoading, setUnlockStatusLoading] = useState(true);

  const loadData = useCallback(async () => {
    try {
      const [packsRes, paypalRes, transRes, unlockRes] = await Promise.all([
        api.get('/payments/credit-packs'),
        api.get('/payments/paypal-client-id'),
        api.get('/payments/transactions'),
        api.get('/payments/unlock-status')
      ]);
      setCreditPacks(packsRes.data.packs);
      setPaypalClientId(paypalRes.data.clientId);
      setTransactions(transRes.data.transactions);
      if (unlockRes.data.unlocked) {
        unlockSenderId();
      }
    } catch (error) {
      console.error('Failed to load data:', error);
      toast.error('Failed to load credit packs');
    } finally {
      setLoading(false);
      setUnlockStatusLoading(false);
    }
  }, [unlockSenderId]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const createOrderWithUnlock = async (shouldIncludeUnlock) => {
    try {
      const response = await api.post('/payments/create-order', {
        packId: selectedPack?.id,
        includeUnlock: shouldIncludeUnlock
      });
      return response.data.orderId;
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to create order');
      throw error;
    }
  };

  const createOrder = () => createOrderWithUnlock(includeUnlock);

  const onApprove = async (data) => {
    try {
      const response = await api.post('/payments/capture-order', {
        orderId: data.orderID
      });

      if (response.data.newBalance !== undefined && response.data.newBalance !== null) {
        updateCredits(response.data.newBalance);
      }
      if (response.data.unlocked) {
        unlockSenderId();
      }
      setNeedsCredits(false);
      setSelectedPack(null);
      setIncludeUnlock(false);
      toast.success(`🎄 ${response.data.message}`);

      // Reload transactions
      const transRes = await api.get('/payments/transactions');
      setTransactions(transRes.data.transactions);
    } catch (error) {
      toast.error(error.response?.data?.error || 'Payment failed. Please try again.');
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString();
  };

  if (loading || unlockStatusLoading) {
    return (
      <div className="main-content">
        <div className="loading">
          <div className="spinner"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="main-content">
      <div className="hero" style={{ padding: '30px 20px' }}>
        <h1 className="hero-title" style={{ fontSize: '2.5rem' }}>
          🎁 Buy Messages
        </h1>
        <p className="hero-subtitle">
          Purchase message packs to send magical Christmas texts
        </p>
        <div className="credits-badge" style={{ margin: '20px auto', width: 'fit-content', fontSize: '1.2rem' }}>
          {user?.credits === 1 ? 'Message' : 'Messages'} Remaining: {user?.credits || 0}
        </div>
        {user?.senderIdUnlocked && (
          <div className="unlock-badge" style={{ margin: '10px auto', width: 'fit-content' }}>
            ✏️ Custom Sender ID Unlocked
          </div>
        )}
      </div>

      {needsCredits && (
        <div className="alert alert-info">
          <span>🎄</span>
          <span>Welcome to Jingle Texts! Purchase a message pack to start sending festive texts to your loved ones!</span>
        </div>
      )}

      {/* Message Packs */}
      <div className="card">
        <h2 className="card-title">🎄 Choose a Message Pack</h2>
        <div className="credit-packs">
          {creditPacks.map(pack => (
            <div 
              key={pack.id}
              className={`credit-pack ${pack.popular ? 'popular' : ''} ${selectedPack?.id === pack.id ? 'selected' : ''}`}
              onClick={() => setSelectedPack(pack)}
              style={{ 
                cursor: 'pointer',
                border: selectedPack?.id === pack.id ? '2px solid var(--christmas-gold)' : undefined
              }}
            >
              <div className="credit-pack-name">{pack.name}</div>
              <div className="credit-pack-credits">
                {pack.credits} <span>{pack.credits === 1 ? 'message' : 'messages'}</span>
              </div>
              <div className="credit-pack-price">£{pack.price.toFixed(2)}</div>
              <div className="credit-pack-description">{pack.description}</div>
              <button 
                className={`btn ${selectedPack?.id === pack.id ? 'btn-gold' : 'btn-primary'}`}
                style={{ marginTop: '15px' }}
              >
                {selectedPack?.id === pack.id ? '✓ Selected' : 'Select'}
              </button>
            </div>
          ))}
        </div>
      </div>

      {/* Custom Sender ID Unlock (standalone) */}
      {!user?.senderIdUnlocked && (
        <div className="card unlock-card">
          <div className="unlock-card-header">
            <span className="unlock-icon">✏️</span>
            <div>
              <h2 className="card-title" style={{ marginBottom: '5px' }}>Unlock Custom Sender ID</h2>
              <p className="unlock-subtitle">Send messages with your own personalised sender name</p>
            </div>
          </div>
          <div className="unlock-features">
            <div className="unlock-feature">✨ Use any alphanumeric sender ID (up to 11 characters)</div>
            <div className="unlock-feature">🎅 Perfect for family names, inside jokes, or personal branding</div>
            <div className="unlock-feature">🔒 Protected by our blacklist to keep your account safe</div>
          </div>
          <div className="unlock-price">£10.00 <span>one-time unlock</span></div>

          {paypalClientId && (
            <div style={{ maxWidth: '400px', margin: '20px auto 0' }}>
              <PayPalScriptProvider options={{
                "client-id": paypalClientId,
                currency: "GBP"
              }}>
                <PayPalButtons
                  style={{
                    layout: 'vertical',
                    color: 'gold',
                    shape: 'pill',
                    label: 'pay'
                  }}
                  createOrder={() => createOrderWithUnlock(true)}
                  onApprove={onApprove}
                  onError={(err) => {
                    console.error('PayPal Unlock Error:', err);
                    toast.error('Payment error. Please try again.');
                  }}
                />
              </PayPalScriptProvider>
            </div>
          )}
        </div>
      )}

      {/* PayPal Checkout */}
      {selectedPack && paypalClientId && (
        <div className="card">
          <h2 className="card-title">💳 Complete Purchase</h2>
          <div style={{ textAlign: 'center', marginBottom: '20px' }}>
            <p style={{ color: 'var(--text-secondary)' }}>
              You're purchasing: <strong style={{ color: 'var(--christmas-gold)' }}>{selectedPack.name}</strong>
            </p>
            <p style={{ color: 'var(--text-secondary)' }}>
              {selectedPack.credits} {selectedPack.credits === 1 ? 'message' : 'messages'} for <strong style={{ color: 'var(--christmas-green-light)' }}>£{selectedPack.price.toFixed(2)}</strong>
            </p>

            {!user?.senderIdUnlocked && (
              <label style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '10px',
                marginTop: '15px',
                padding: '12px',
                background: 'rgba(255,255,255,0.05)',
                borderRadius: '10px',
                cursor: 'pointer',
                maxWidth: '400px',
                margin: '15px auto 0'
              }}>
                <input
                  type="checkbox"
                  checked={includeUnlock}
                  onChange={(e) => setIncludeUnlock(e.target.checked)}
                />
                <span>✏️ Also unlock Custom Sender ID (+£10.00)</span>
              </label>
            )}

            {includeUnlock && (
              <p style={{ color: 'var(--christmas-gold)', marginTop: '15px', fontWeight: 'bold' }}>
                Total: £{(selectedPack.price + 10).toFixed(2)}
              </p>
            )}
          </div>

          <div style={{ maxWidth: '400px', margin: '0 auto' }}>
            <PayPalScriptProvider options={{
              "client-id": paypalClientId,
              currency: "GBP"
            }}>
              <PayPalButtons
                style={{
                  layout: 'vertical',
                  color: 'gold',
                  shape: 'pill',
                  label: 'pay'
                }}
                createOrder={createOrder}
                onApprove={onApprove}
                onError={(err) => {
                  console.error('PayPal Error:', err);
                  toast.error('Payment error. Please try again.');
                }}
              />
            </PayPalScriptProvider>
          </div>

          <button
            onClick={() => {
              setSelectedPack(null);
              setIncludeUnlock(false);
            }}
            className="btn btn-outline"
            style={{ display: 'block', margin: '20px auto 0' }}
          >
            Cancel
          </button>
        </div>
      )}

      {/* Transaction History */}
      {transactions.length > 0 && (
        <div className="card">
          <h2 className="card-title">📜 Purchase History</h2>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ borderBottom: '1px solid var(--border-color)' }}>
                  <th style={{ padding: '10px', textAlign: 'left', color: 'var(--text-secondary)' }}>Date</th>
                  <th style={{ padding: '10px', textAlign: 'left', color: 'var(--text-secondary)' }}>Pack</th>
                  <th style={{ padding: '10px', textAlign: 'center', color: 'var(--text-secondary)' }}>Credits</th>
                  <th style={{ padding: '10px', textAlign: 'right', color: 'var(--text-secondary)' }}>Amount</th>
                  <th style={{ padding: '10px', textAlign: 'center', color: 'var(--text-secondary)' }}>Status</th>
                </tr>
              </thead>
              <tbody>
                {transactions.map(t => (
                  <tr key={t.id} style={{ borderBottom: '1px solid rgba(255,255,255,0.05)' }}>
                    <td style={{ padding: '10px' }}>{formatDate(t.createdAt)}</td>
                    <td style={{ padding: '10px' }}>
                      {t.type === 'unlock' && '🔓 Custom Sender Unlock'}
                      {t.type === 'combined' && `🔓 Custom Sender Unlock + ${t.packId}`}
                      {t.type === 'credits' && t.packId}
                      {!t.type && t.packId}
                    </td>
                    <td style={{ padding: '10px', textAlign: 'center' }}>{t.credits || '—'}</td>
                    <td style={{ padding: '10px', textAlign: 'right' }}>£{t.amount.toFixed(2)}</td>
                    <td style={{ padding: '10px', textAlign: 'center' }}>
                      <span style={{ 
                        padding: '4px 12px', 
                        borderRadius: '10px',
                        background: t.status === 'completed' ? 'rgba(34,139,34,0.2)' : 'rgba(255,165,0,0.2)',
                        color: t.status === 'completed' ? '#90EE90' : 'orange',
                        fontSize: '0.85rem'
                      }}>
                        {t.status}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default Credits;
